import React, { lazy, Suspense, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  getPickLists,
  getPickList,
  getOrders,
  listStockMoves,
  executeStockMove,
  stockMoveReasonCodes,
  getReceipts,
  getReceipt,
  listCycleCounts,
  getCycleCount,
  createCycleCount,
} from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useCompany } from '../context/CompanyContext';
import { playSuccessTone, playErrorTone } from '../lib/audioFeedback';
import { useVoice } from '../hooks/useVoice';
import OperatorHeader from '../components/operator/OperatorHeader';
import QueueView from '../components/operator/QueueView';

// Each working flow is loaded on demand so the initial Operator Console
// payload is essentially Header + QueueView. Meaningful on RF guns with
// flaky tethered uplinks — the queue loads in one round-trip and the
// flow's bundle only flies once the operator taps into it.
const PickFlow = lazy(() => import('../components/operator/PickFlow'));
const ShipFlow = lazy(() => import('../components/operator/ShipFlow'));
const MoveFlow = lazy(() => import('../components/operator/MoveFlow'));
const ReceiveFlow = lazy(() => import('../components/operator/ReceiveFlow'));
const CycleCountFlow = lazy(() => import('../components/operator/CycleCountFlow'));

const FlowFallback = ({ header }) => (
  <div className="min-h-screen bg-zinc-100 flex flex-col" data-testid="operator-flow-loading">
    {header}
    <div className="flex-1 flex items-center justify-center text-zinc-500 text-sm uppercase tracking-widest font-bold animate-pulse">
      Loading flow…
    </div>
  </div>
);

/**
 * OperatorConsole — full-screen warehouse-floor workflow orchestrator.
 *
 * Owns the queue data and the shared header/voice state. Each working view
 * (pick / ship / move / receive / count) is a self-contained subcomponent.
 */
export default function OperatorConsole() {
  const navigate = useNavigate();
  const { user, isPicker } = useAuth();
  const { company } = useCompany();
  const isAdmin = user?.role === 'admin' || user?.role === 'manager';

  const [view, setView] = useState('queue');
  const [pickLists, setPickLists] = useState([]);
  const [readyOrders, setReadyOrders] = useState([]);
  const [pendingMoves, setPendingMoves] = useState([]);
  const [pendingReceipts, setPendingReceipts] = useState([]);
  const [pendingCounts, setPendingCounts] = useState([]);
  const [moveReasonCodes, setMoveReasonCodes] = useState([]);
  const [activePick, setActivePick] = useState(null);
  const [activeShipOrder, setActiveShipOrder] = useState(null);
  const [activeReceipt, setActiveReceipt] = useState(null);
  const [activeCount, setActiveCount] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const { voiceOn, toggleVoice, speak } = useVoice();

  const skipPack = !!company?.skip_pack_step;
  const autoPrint = !!company?.auto_print_on_ship;
  const printMode = company?.print_mode || 'browser';

  const refresh = async () => {
    try {
      const params = isPicker ? { picker_id: user?.id } : {};
      const [pl, packed, moves, rc, receipts, ccPending, ccProgress] = await Promise.all([
        getPickLists({ ...params }),
        getOrders({ status: 'packed' }),
        listStockMoves({ status: 'pending' }),
        moveReasonCodes.length ? Promise.resolve({ data: { reason_codes: moveReasonCodes } }) : stockMoveReasonCodes(),
        getReceipts({ status: 'pending' }),
        listCycleCounts({ status: 'pending' }),
        listCycleCounts({ status: 'in_progress' }),
      ]);
      setPickLists((pl?.data || []).filter((p) => p.status !== 'completed'));
      setReadyOrders(packed?.data || []);
      setPendingMoves(moves?.data || []);
      const inProgress = await getReceipts({ status: 'in_progress' });
      setPendingReceipts([...(receipts?.data || []), ...(inProgress?.data || [])]);
      setPendingCounts([...(ccPending?.data || []), ...(ccProgress?.data || [])]);
      if (!moveReasonCodes.length) setMoveReasonCodes(rc?.data?.reason_codes || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const exit = () => navigate('/');

  // ============================================================
  //  Queue handlers (load entity, then transition view)
  // ============================================================
  const onStartPick = async (pl) => {
    try {
      setBusy(true);
      const r = await getPickList(pl.id);
      setActivePick(r.data);
      setView('pick');
    } catch (_) {
      toast.error('Failed to load pick list');
    } finally {
      setBusy(false);
    }
  };

  const onStartShip = (o) => {
    setActiveShipOrder(o);
    setView('ship');
  };

  const onStartMove = () => setView('move');

  const onExecutePlannedMove = async (move) => {
    if (busy) return;
    setBusy(true);
    try {
      await executeStockMove(move.id);
      playSuccessTone();
      speak('Move executed');
      toast.success(`Move ${move.move_number} executed`);
      await refresh();
    } catch (e) {
      playErrorTone();
      toast.error(e?.response?.data?.detail || 'Execute failed');
    } finally {
      setBusy(false);
    }
  };

  const onStartReceive = async (receipt) => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await getReceipt(receipt.id);
      setActiveReceipt(r.data);
      setView('receive');
    } catch (_) {
      toast.error('Failed to load receipt');
    } finally {
      setBusy(false);
    }
  };

  const onStartCount = async (cc) => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await getCycleCount(cc.id);
      setActiveCount(r.data);
      setView('count');
    } catch (_) {
      toast.error('Failed to load cycle count');
    } finally {
      setBusy(false);
    }
  };

  const onStartQuickCount = async () => {
    if (busy || !isAdmin) return;
    setBusy(true);
    try {
      const r = await createCycleCount({ count_type: 'spot' });
      setActiveCount(r.data);
      setView('count');
      toast.success(`Started ad-hoc count ${r.data.count_number}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start count');
    } finally {
      setBusy(false);
    }
  };

  // ============================================================
  //  Flow exits → reset active entity, refresh queue, return to queue view
  // ============================================================
  const exitToQueue = async (clearKey) => {
    if (clearKey === 'pick') setActivePick(null);
    else if (clearKey === 'ship') setActiveShipOrder(null);
    else if (clearKey === 'receive') setActiveReceipt(null);
    else if (clearKey === 'count') setActiveCount(null);
    setView('queue');
    await refresh();
  };

  // skip-pack: jump straight to ship view for the just-completed order
  const onPickCompleteToShip = async (orderIds) => {
    await refresh();
    try {
      const pk = await getOrders({ status: 'packed' });
      const target = (pk?.data || []).find((o) => orderIds.includes(o.id));
      if (target) {
        setActivePick(null);
        setActiveShipOrder(target);
        setView('ship');
        return;
      }
    } catch (_) { /* fall through to queue */ }
    await exitToQueue('pick');
  };

  // ============================================================
  //  Render
  // ============================================================
  const header = (
    <OperatorHeader
      view={view}
      activePick={activePick}
      activeShipOrder={activeShipOrder}
      activeReceipt={activeReceipt}
      activeCount={activeCount}
      voiceOn={voiceOn}
      toggleVoice={toggleVoice}
      onExit={exit}
      skipPack={skipPack}
      autoPrint={autoPrint}
      printMode={printMode}
    />
  );

  if (view === 'pick' && activePick) {
    return (
      <Suspense fallback={<FlowFallback header={header} />}>
        <PickFlow
          header={header}
          initialPick={activePick}
          voiceOn={voiceOn}
          speak={speak}
          skipPack={skipPack}
          onExit={() => exitToQueue('pick')}
          onCompleteToShip={onPickCompleteToShip}
        />
      </Suspense>
    );
  }

  if (view === 'ship' && activeShipOrder) {
    return (
      <Suspense fallback={<FlowFallback header={header} />}>
        <ShipFlow
          header={header}
          order={activeShipOrder}
          voiceOn={voiceOn}
          speak={speak}
          autoPrint={autoPrint}
          onExit={() => exitToQueue('ship')}
        />
      </Suspense>
    );
  }

  if (view === 'move') {
    return (
      <Suspense fallback={<FlowFallback header={header} />}>
        <MoveFlow
          header={header}
          voiceOn={voiceOn}
          speak={speak}
          initialReasonCodes={moveReasonCodes}
          onExit={() => exitToQueue()}
          onMoved={refresh}
        />
      </Suspense>
    );
  }

  if (view === 'receive' && activeReceipt) {
    return (
      <Suspense fallback={<FlowFallback header={header} />}>
        <ReceiveFlow
          header={header}
          initialReceipt={activeReceipt}
          voiceOn={voiceOn}
          speak={speak}
          onExit={() => exitToQueue('receive')}
        />
      </Suspense>
    );
  }

  if (view === 'count' && activeCount) {
    return (
      <Suspense fallback={<FlowFallback header={header} />}>
        <CycleCountFlow
          header={header}
          initialCount={activeCount}
          voiceOn={voiceOn}
          speak={speak}
          onExit={() => exitToQueue('count')}
        />
      </Suspense>
    );
  }

  return (
    <QueueView
      header={header}
      loading={loading}
      busy={busy}
      isAdmin={isAdmin}
      pickLists={pickLists}
      readyOrders={readyOrders}
      pendingReceipts={pendingReceipts}
      pendingMoves={pendingMoves}
      pendingCounts={pendingCounts}
      onRefresh={refresh}
      onStartPick={onStartPick}
      onStartShip={onStartShip}
      onStartReceive={onStartReceive}
      onStartMove={onStartMove}
      onExecutePlannedMove={onExecutePlannedMove}
      onStartCount={onStartCount}
      onStartQuickCount={onStartQuickCount}
    />
  );
}
